/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import { Box, MenuItem, Paper, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import { useForm } from "@/src/provider/Form/FormProvider";
import React from "react";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { dateFieldDefault, isRequired, maxStringLength, minStringLength, pastDateDefault } from "@/src/services/validationRules";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { useLocation, useNavigate } from "react-router";
import StatusDivorceModal from "@/src/components/FormInfoModal/StatusDivorceModal";
import SeparationDivorceModal from "@/src/components/FormInfoModal/SeparationModal";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import { modalview, pageview } from "@/src/services/useTracker";
import { translate } from "@/src/services/translations/translations";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";
import InfoIconButton from "@/src/components/Button/InfoIconButton";

type DivorceProps = {
  submitProps: FormSubmitProps;
};

export default function Divorce({ submitProps }: DivorceProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { values, handleSubmit } = useForm();

  const handleStatusModalOpen = () => {
    navigate(location.pathname, { state: { statusDivorceModalOpen: true } });
    modalview("Status Divorce Help Modal");
  };

  const handleSeperationModalOpen = () => {
    navigate(location.pathname, { state: { separationDivorceModalOpen: true } });
    modalview("Separation Year Help Modal");
  };

  const handleModalClose = () => {
    navigate(-1);
    pageview(location.pathname);
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ehe und Scheidung
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>Wann und wo haben Sie standesamtlich geheiratet?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                name="dateMarriage"
                label={translate("divorce.dateMarriage.label", values)}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie das Datum der standesamtlichen Heirat an",
                  },
                  dateFieldDefault,
                  pastDateDefault,
                ]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="cityMarriage"
                label={translate("divorce.cityMarriage.label", values)}
                validators={[
                  { validator: isRequired, message: "Bitte geben Sie den Ort Ihrer Heirat an" },
                  { validator: minStringLength, params: [2], message: "Bitte kontrollieren Sie Ihre Angabe" },
                  { validator: maxStringLength, params: [30], message: "Bitte kontrollieren Sie Ihre Angabe" },
                ]}
              />
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{translate("divorce.numberChildren.question", values)}</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorSelectField
                label={translate("divorce.numberChildren.label", values)}
                name="numberChildren"
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie die Anzahl Ihrer gemeinsamen Kinder unter 18 Jahre an",
                  },
                ]}
              >
                <MenuItem value={"0"}>{translate("divorce.numberChildren.options.0.label", values)}</MenuItem>
                <MenuItem value={"1"}>{translate("divorce.numberChildren.options.1.label", values)}</MenuItem>
                <MenuItem value={"2"}>{translate("divorce.numberChildren.options.2.label", values)}</MenuItem>
                <MenuItem value={"3"}>{translate("divorce.numberChildren.options.3.label", values)}</MenuItem>
                <MenuItem value={"4"}>{translate("divorce.numberChildren.options.4.label", values)}</MenuItem>
                <MenuItem value={"5"}>{translate("divorce.numberChildren.options.5.label", values)}</MenuItem>
                <MenuItem value={"6"}>{translate("divorce.numberChildren.options.6.label", values)}</MenuItem>
              </ValidatorSelectField>
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{translate("divorce.statusDivorce.question", values)}</FormLegend>
            </Grid>
            <Grid size={10}>
              <ValidatorSelectField
                label={translate("divorce.statusDivorce.label", values)}
                name="statusDivorce"
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie an, ob Ihre Scheidung einvernehmlich ist",
                  },
                ]}
              >
                <MenuItem value="mutuallyAgreed">{translate("divorce.statusDivorce.options.mutuallyAgreed.label", values)}</MenuItem>
                <MenuItem value="debatable">{translate("divorce.statusDivorce.options.debatable.label", values)}</MenuItem>
                <MenuItem value="unsure">{translate("divorce.statusDivorce.options.unsure.label", values)}</MenuItem>
              </ValidatorSelectField>
              <StatusDivorceModal open={!!(location.state && location.state.statusDivorceModalOpen)} onClose={handleModalClose} />
            </Grid>
            <Grid size={2}>
              <Stack
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <InfoIconButton onClick={handleStatusModalOpen} />
              </Stack>
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>Wie weit sind Sie bezüglich Ihrer Trennung fortgeschritten?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                name="dateSeparation"
                label={translate("divorce.dateSeparation.label", values)}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie das Datum der Trennung an",
                  },
                  dateFieldDefault,
                  pastDateDefault,
                ]}
              />
            </Grid>
            <Grid size={10}>
              <ValidatorSelectField
                label={translate("divorce.movedOut.label", values)}
                name="movedOut"
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie Ihre aktuelle Wohnsituation an",
                  },
                ]}
              >
                <MenuItem value="petitioner">{translate("divorce.movedOut.options.petitioner.label", values)}</MenuItem>
                <MenuItem value="partner">{translate("divorce.movedOut.options.partner.label", values)}</MenuItem>
                <MenuItem value="both">{translate("divorce.movedOut.options.both.label", values)}</MenuItem>
                <MenuItem value="nobody">{translate("divorce.movedOut.options.nobody.label", values)}</MenuItem>
              </ValidatorSelectField>
              <SeparationDivorceModal open={!!(location.state && location.state.separationDivorceModalOpen)} onClose={handleModalClose} />
            </Grid>
            <Grid size={2}>
              <Stack
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <InfoIconButton onClick={handleSeperationModalOpen} />
              </Stack>
            </Grid>
            {values.movedOut && values.movedOut !== "nobody" && (
              <Grid size={12}>
                <ValidatorDateField
                  name="dateMovedOut"
                  label={translate("divorce.dateMovedOut.label", values)}
                  validators={[
                    {
                      validator: isRequired,
                      message: "Bitte geben Sie das Datum des Auszugs an",
                    },
                    dateFieldDefault,
                    pastDateDefault,
                  ]}
                />
              </Grid>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
